import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { AccountSelectors } from '@recruitee/account';
import { filterBoolean, LocalStorageService } from '@recruitee/common';
import type { Company } from '@recruitee/company-types';
import { I18nService } from '@recruitee/i18n';
import type { Country } from '@recruitee/locations-types';
import { SelectGroupedOptions, SelectGroupOption } from '@recruitee/user-interface';

@Injectable({ providedIn: 'root' })
export class LocationsService {
  private readonly PREFERRED_LOCALES_KEY: string = 'PreferredLocales.items';
  private readonly maxPreferredLocalesCount: number = 3;
  private preferredLocales: string[];
  private companyCountryCode: string;

  constructor(
    private localStorage: LocalStorageService,
    private i18n: I18nService,
    private store: Store,
  ) {
    this.preferredLocales = this.getPreferredLocales();
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean)
      .subscribe(({ countryCode }: Company) => {
        this.companyCountryCode = countryCode!;
        this.preferredLocales = this.getPreferredLocales();
      });
  }

  public setPreferredLocales(locale: string): void {
    if (!this.preferredLocales || this.preferredLocales.includes(locale)) return;

    this.preferredLocales = [
      this.companyCountryCode,
      locale,
      ...this.preferredLocales.filter(v => v !== this.companyCountryCode),
    ]
      .filter(Boolean)
      .slice(0, this.maxPreferredLocalesCount);
    this.localStorage.set(this.PREFERRED_LOCALES_KEY, this.preferredLocales);
  }

  public getGroupedCountries(countries: Country[]): SelectGroupedOptions[] {
    const countriesMap = countries.reduce<Record<string, Country>>((agg, country) => {
      agg[country.code] = country;
      return agg;
    }, {});

    const preferredGroup = {
      title: null,
      options: this.preferredLocales.reduce(
        (acc, locale) => {
          const country = countriesMap[locale];
          return country
            ? [
                ...acc,
                {
                  label: country.name,
                  value: country.code,
                  additionalSearchKeys: [country.code],
                },
              ]
            : acc;
        },
        <SelectGroupOption[]>[],
      ),
    };

    const allCountriesGroup = {
      title: preferredGroup.options.length
        ? this.i18n.translate('app.offer_form.all_countries')
        : null,
      options: countries
        .filter(c => !this.preferredLocales.includes(c.code))
        .map(country => ({
          label: country.name,
          value: country.code,
          additionalSearchKeys: [country.code],
        })),
    };

    return [preferredGroup, allCountriesGroup];
  }

  private getPreferredLocales(): string[] {
    return [this.companyCountryCode, ...this.localStorage.get(this.PREFERRED_LOCALES_KEY, [])]
      .filter((value, index, array) => array.indexOf(value) === index && !!value)
      .slice(0, this.maxPreferredLocalesCount);
  }
}
